import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { storageTypeFields } from "encamp-shared/src/utils/storageTypeFields";
import {
  InventoryOverlapFieldResolutionInput,
  InventoryOverlapFieldResolutionScope,
  InventoryOverlapResolutionInput,
  InventoryOverlapResolutionStrategy,
  InventoryOverlapStorageLocationAttribute,
  Pressure,
  Temperature,
  TierIiReportEncampStatus,
} from "generated-graphql/graphql";

const PRESSURE_VALUES = new Set<string>(Object.values(Pressure));
const TEMPERATURE_VALUES = new Set<string>(Object.values(Temperature));

function isPressureValue(value: string | null): value is Pressure {
  return value !== null && PRESSURE_VALUES.has(value);
}

function isTemperatureValue(value: string | null): value is Temperature {
  return value !== null && TEMPERATURE_VALUES.has(value);
}

export type InventoryOverlapCandidate = {
  id: string;
  name: string;
  facilityChemicalStateFields?:
    | {
        facilityId: string;
        reportingYear: number;
        jurisdiction: string;
        key: string;
        label?: string | null;
        value?: unknown;
      }[]
    | null;
  facilityChemicals?:
    | {
        facilityId: string;
        reportingYear: number;
        maxAmount?: number | null;
        unit?: string | null;
        facility?: {
          name: string;
          tierIIReports?: { reportingYear: number; encampStatus: string }[] | null;
        } | null;
        storageLocations?:
          | {
              storageLocationId: string;
              maxAmount?: number | null;
              averageAmount?: number | null;
              unit?: string | null;
              pressure?: string | null;
              otherPressureValue?: string | null;
              temperature?: string | null;
              storageType?: string | null;
              storageTypes?: string[] | null;
              storageTypeDescription?: string | null;
              storageLocation?: { description?: string | null } | null;
            }[]
          | null;
      }[]
    | null;
};

export type InventoryOverlapStorageLocationDetail = {
  storageLocationId: string;
  name: string;
  maxAmount: number | null;
  averageAmount: number | null;
  unit: string | null;
  pressure: string | null;
  otherPressureValue: string | null;
  temperature: string | null;
  storageTypes: string[];
  storageTypeDescription: string | null;
};

export type InventoryOverlapStateFieldDetail = {
  jurisdiction: string;
  key: string;
  label: string | null;
  value: unknown;
};

export type InventoryOverlapEntry = {
  chemicalId: string;
  chemicalName: string;
  maxAmount: number | null;
  unit: string | null;
  storageLocationNames: string[];
  storageLocations: InventoryOverlapStorageLocationDetail[];
  stateFields: InventoryOverlapStateFieldDetail[];
};

export type InventoryOverlap = {
  facilityId: string;
  facilityName: string;
  reportingYear: number;
  entries: InventoryOverlapEntry[];
  hasIdenticalAmounts: boolean;
  hasSharedUnit: boolean;
  isFiled: boolean;
};

export function inventoryOverlapKey(
  inventoryOverlap: Pick<InventoryOverlap, "facilityId" | "reportingYear">
): string {
  return `${inventoryOverlap.facilityId}|${inventoryOverlap.reportingYear}`;
}

export function formatEntryAmount(entry: {
  maxAmount: number | null;
  unit: string | null;
}): string {
  if (entry.maxAmount === null) return "—";

  const amountLabel = entry.maxAmount.toLocaleString();
  if (!entry.unit) return amountLabel;

  return `${amountLabel} ${prettyPrintEnumValue(entry.unit).toLowerCase()}`;
}

function computeHasIdenticalAmounts(entries: InventoryOverlapEntry[]): boolean {
  if (entries.length === 0) return false;
  if (entries.some((entry) => entry.maxAmount === null)) return false;

  const [first, ...rest] = entries;
  return rest.every((entry) => entry.maxAmount === first.maxAmount && entry.unit === first.unit);
}

/**
 * True when combining could sum every amount without a unit conversion: the
 * chemical-level units agree, and — mirroring the backend's combineAmount —
 * the amount-bearing rows of every multi-row storage-location identity group
 * agree on a unit. A chemical-level match can still hide a per-location
 * split (e.g. both entries say pounds overall but share a location where one
 * stores gallons), which would dead-end the merge server-side if COMBINE
 * were offered.
 */
function computeHasSharedUnit(entries: InventoryOverlapEntry[]): boolean {
  if (new Set(entries.map((entry) => entry.unit ?? null)).size > 1) {
    return false;
  }

  const unitsByGroup = new Map<string, { rowCount: number; units: Set<string> }>();
  for (const entry of entries) {
    for (const row of entry.storageLocations) {
      const groupKey = storageLocationGroupIdentityKey(row);
      let group = unitsByGroup.get(groupKey);
      if (!group) {
        group = { rowCount: 0, units: new Set() };
        unitsByGroup.set(groupKey, group);
      }
      group.rowCount += 1;
      if ((row.maxAmount != null || row.averageAmount != null) && row.unit != null) {
        group.units.add(row.unit);
      }
    }
  }
  return Array.from(unitsByGroup.values()).every(
    (group) => group.rowCount === 1 || group.units.size <= 1
  );
}

/**
 * Finds (facility, reporting year) pairs where more than one merge candidate
 * has a facility chemical entry. Each such inventory overlap must be resolved
 * (KEEP one entry, or COMBINE them) before the merge can proceed.
 */
export function findInventoryOverlaps(candidates: InventoryOverlapCandidate[]): InventoryOverlap[] {
  const entriesByFacilityYear = new Map<
    string,
    {
      facilityId: string;
      facilityName: string;
      reportingYear: number;
      isFiled: boolean;
      entries: Map<string, InventoryOverlapEntry>;
    }
  >();

  for (const candidate of candidates) {
    for (const facilityChemical of candidate.facilityChemicals ?? []) {
      const key = inventoryOverlapKey(facilityChemical);
      let entry = entriesByFacilityYear.get(key);
      if (!entry) {
        entry = {
          facilityId: facilityChemical.facilityId,
          facilityName: facilityChemical.facility?.name ?? "Unknown facility",
          reportingYear: facilityChemical.reportingYear,
          isFiled: false,
          entries: new Map(),
        };
        entriesByFacilityYear.set(key, entry);
      }
      entry.isFiled =
        entry.isFiled ||
        (facilityChemical.facility?.tierIIReports ?? []).some(
          (report) =>
            report.reportingYear === facilityChemical.reportingYear &&
            report.encampStatus === TierIiReportEncampStatus.FilingComplete
        );
      const storageLocations = (facilityChemical.storageLocations ?? []).map((sl) => ({
        storageLocationId: sl.storageLocationId,
        name: sl.storageLocation?.description ?? "Unnamed location",
        maxAmount: sl.maxAmount ?? null,
        averageAmount: sl.averageAmount ?? null,
        unit: sl.unit ?? null,
        pressure: sl.pressure ?? null,
        otherPressureValue: sl.otherPressureValue ?? null,
        temperature: sl.temperature ?? null,
        storageTypes: storageTypeFields(sl).storageTypes,
        storageTypeDescription: sl.storageTypeDescription ?? null,
      }));
      entry.entries.set(candidate.id, {
        chemicalId: candidate.id,
        chemicalName: candidate.name,
        maxAmount: facilityChemical.maxAmount ?? null,
        unit: facilityChemical.unit ?? null,
        storageLocationNames: Array.from(
          new Set(
            (facilityChemical.storageLocations ?? [])
              .map((storageLocation) => storageLocation.storageLocation?.description)
              .filter((description): description is string => !!description)
          )
        ),
        storageLocations,
        stateFields: (candidate.facilityChemicalStateFields ?? [])
          .filter(
            (sf) =>
              sf.facilityId === facilityChemical.facilityId &&
              sf.reportingYear === facilityChemical.reportingYear
          )
          .map((sf) => ({
            jurisdiction: sf.jurisdiction,
            key: sf.key,
            label: sf.label ?? null,
            value: sf.value,
          })),
      });
    }
  }

  return Array.from(entriesByFacilityYear.values())
    .filter((entry) => entry.entries.size > 1)
    .map((entry) => {
      const entries = Array.from(entry.entries.values());
      return {
        facilityId: entry.facilityId,
        facilityName: entry.facilityName,
        reportingYear: entry.reportingYear,
        entries,
        hasIdenticalAmounts: computeHasIdenticalAmounts(entries),
        hasSharedUnit: computeHasSharedUnit(entries),
        isFiled: entry.isFiled,
      };
    })
    .sort(
      (a, b) => a.facilityName.localeCompare(b.facilityName) || a.reportingYear - b.reportingYear
    );
}

/**
 * Counts distinct (facility, reporting year) pairs across all candidates,
 * whether or not they overlap. Used to report how many facility-years merge
 * automatically (i.e. don't require a decision).
 */
export function countDistinctFacilityYears(candidates: InventoryOverlapCandidate[]): number {
  const keys = new Set<string>();
  for (const candidate of candidates) {
    for (const facilityChemical of candidate.facilityChemicals ?? []) {
      keys.add(inventoryOverlapKey(facilityChemical));
    }
  }
  return keys.size;
}

export type StorageLocationGroupIdentity = {
  storageLocationId: string;
  storageTypes: string[];
  storageTypeDescription: string | null;
};

export function storageLocationConflictKey(
  group: StorageLocationGroupIdentity,
  attribute: InventoryOverlapStorageLocationAttribute
): string {
  const sortedStorageTypes = [...group.storageTypes].sort();
  return `SL|${group.storageLocationId}|${JSON.stringify(sortedStorageTypes)}|${
    group.storageTypeDescription ?? ""
  }|${attribute}`;
}

export function stateFieldConflictKey(jurisdiction: string, key: string): string {
  return `SF|${jurisdiction}|${key}`;
}

type ConflictOption = { chemicalId: string; chemicalName: string; display: string };

// A distinct value held by one or more rows in a combined storage-location
// group. `resolutionFields` are the typed fields to emit on the mutation
// when this option wins (spread directly — never re-derived from
// `serializedValue`, which exists only to key the UI's stored choice).
export type StorageLocationConflictOption = {
  serializedValue: string;
  display: string;
  chemicalNames: string[];
  resolutionFields: Pick<
    InventoryOverlapFieldResolutionInput,
    "pressure" | "otherPressureValue" | "temperature"
  >;
};

export type StorageLocationCombine = StorageLocationGroupIdentity & {
  storageLocationName: string;
  context: { label: string; display: string }[];
  conflicts: {
    attribute: InventoryOverlapStorageLocationAttribute;
    options: StorageLocationConflictOption[];
  }[];
};

export type StateFieldConflict = {
  jurisdiction: string;
  key: string;
  label: string;
  options: ConflictOption[];
};

export type CombineConflicts = {
  storageLocations: StorageLocationCombine[];
  stateFieldConflicts: StateFieldConflict[];
};

type StorageLocationCluster = {
  attribute: InventoryOverlapStorageLocationAttribute;
  label: string;
  hasValue: (row: InventoryOverlapStorageLocationDetail) => boolean;
  resolutionFields: (
    row: InventoryOverlapStorageLocationDetail
  ) => StorageLocationConflictOption["resolutionFields"];
  display: (row: InventoryOverlapStorageLocationDetail) => string;
};

function displayEnum(value: string | null): string {
  return value ? prettyPrintEnumValue(value) : "—";
}

// Mirrors the backend's attribute clusters in
// apps/api/src/resolvers/chemical/combineInventoryOverlaps.ts — the merge is
// rejected if a conflict detected there has no resolution built here. Amount
// is not a cluster: it always sums (never picked). Storage type is not a
// cluster either: it is part of the group's identity (see
// storageLocationGroupIdentityKey), not a pickable conflict.
const STORAGE_LOCATION_CLUSTERS: StorageLocationCluster[] = [
  {
    attribute: InventoryOverlapStorageLocationAttribute.Pressure,
    label: "Pressure",
    hasValue: (row) => row.pressure != null,
    resolutionFields: (row) => ({
      pressure: isPressureValue(row.pressure) ? row.pressure : null,
      otherPressureValue: row.otherPressureValue,
    }),
    display: (row) =>
      row.otherPressureValue
        ? `${displayEnum(row.pressure)} (${row.otherPressureValue})`
        : displayEnum(row.pressure),
  },
  {
    attribute: InventoryOverlapStorageLocationAttribute.Temperature,
    label: "Temperature",
    hasValue: (row) => row.temperature != null,
    resolutionFields: (row) => ({
      temperature: isTemperatureValue(row.temperature) ? row.temperature : null,
    }),
    display: (row) => displayEnum(row.temperature),
  },
];

/**
 * A storage-location row's identity for combine purposes — mirrors the
 * backend's storageLocationGroupKey. Rows only combine when they share a
 * location AND a (sorted) storage-type set AND a description; storage type
 * is identity, not a conflict, so one location can produce multiple combined
 * rows.
 */
function storageLocationGroupIdentityKey(identity: StorageLocationGroupIdentity): string {
  const sortedStorageTypes = [...identity.storageTypes].sort();
  return `${identity.storageLocationId}|${JSON.stringify(sortedStorageTypes)}|${
    identity.storageTypeDescription ?? ""
  }`;
}

function displayStateFieldValue(value: unknown): string {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value === null || value === undefined) return "—";
  return String(value);
}

/**
 * Detects the field conflicts a COMBINE resolution must settle: attributes
 * that genuinely differ on a storage location both chemicals reference, and
 * (jurisdiction, key) state fields where both chemicals have differing
 * values. Matching or one-sided values are returned as read-only context (or
 * omitted) — they never require a pick.
 */
export function findCombineConflicts(inventoryOverlap: InventoryOverlap): CombineConflicts {
  const rowsByGroup = new Map<
    string,
    StorageLocationGroupIdentity & {
      name: string;
      rows: {
        chemicalId: string;
        chemicalName: string;
        row: InventoryOverlapStorageLocationDetail;
      }[];
    }
  >();
  for (const entry of inventoryOverlap.entries) {
    for (const row of entry.storageLocations) {
      const identity: StorageLocationGroupIdentity = {
        storageLocationId: row.storageLocationId,
        storageTypes: [...row.storageTypes].sort(),
        storageTypeDescription: row.storageTypeDescription,
      };
      const groupKey = storageLocationGroupIdentityKey(identity);
      let group = rowsByGroup.get(groupKey);
      if (!group) {
        group = { ...identity, name: row.name, rows: [] };
        rowsByGroup.set(groupKey, group);
      }
      group.rows.push({ chemicalId: entry.chemicalId, chemicalName: entry.chemicalName, row });
    }
  }

  const storageLocations: StorageLocationCombine[] = [];
  for (const group of rowsByGroup.values()) {
    // A lone row (even from a chemical with just one row here) has nothing
    // to combine with. Multi-row groups can conflict even when every row
    // belongs to the same chemical (per-container rows sharing an identity).
    if (group.rows.length <= 1) continue;

    const context: StorageLocationCombine["context"] = [];
    const conflicts: StorageLocationCombine["conflicts"] = [];

    const amountRows = group.rows.filter((r) => r.row.maxAmount != null);
    if (amountRows.length > 0) {
      const unit = amountRows.find((r) => r.row.unit)?.row.unit ?? null;
      const total = amountRows.reduce((sum, r) => sum + (r.row.maxAmount ?? 0), 0);
      context.push({ label: "Amount", display: formatEntryAmount({ maxAmount: total, unit }) });
    }

    for (const cluster of STORAGE_LOCATION_CLUSTERS) {
      const valued = group.rows.filter((r) => cluster.hasValue(r.row));
      if (valued.length === 0) continue;

      const optionsBySerializedValue = new Map<
        string,
        {
          display: string;
          chemicalNames: Set<string>;
          resolutionFields: StorageLocationConflictOption["resolutionFields"];
        }
      >();
      for (const valuedRow of valued) {
        const resolutionFields = cluster.resolutionFields(valuedRow.row);
        const serializedValue = JSON.stringify(resolutionFields);
        let option = optionsBySerializedValue.get(serializedValue);
        if (!option) {
          option = {
            display: cluster.display(valuedRow.row),
            chemicalNames: new Set(),
            resolutionFields,
          };
          optionsBySerializedValue.set(serializedValue, option);
        }
        option.chemicalNames.add(valuedRow.chemicalName);
      }

      if (optionsBySerializedValue.size <= 1) {
        context.push({ label: cluster.label, display: cluster.display(valued[0].row) });
      } else {
        conflicts.push({
          attribute: cluster.attribute,
          options: Array.from(optionsBySerializedValue.entries()).map(
            ([serializedValue, option]) => ({
              serializedValue,
              display: option.display,
              chemicalNames: Array.from(option.chemicalNames),
              resolutionFields: option.resolutionFields,
            })
          ),
        });
      }
    }
    if (conflicts.length > 0) {
      storageLocations.push({
        storageLocationId: group.storageLocationId,
        storageLocationName: group.name,
        storageTypes: group.storageTypes,
        storageTypeDescription: group.storageTypeDescription,
        context,
        conflicts,
      });
    }
  }

  const optionsByField = new Map<
    string,
    {
      jurisdiction: string;
      key: string;
      label: string;
      options: ConflictOption[];
      values: Set<string>;
    }
  >();
  for (const entry of inventoryOverlap.entries) {
    for (const stateField of entry.stateFields) {
      const key = stateFieldConflictKey(stateField.jurisdiction, stateField.key);
      let field = optionsByField.get(key);
      if (!field) {
        field = {
          jurisdiction: stateField.jurisdiction,
          key: stateField.key,
          label: stateField.label ?? stateField.key,
          options: [],
          values: new Set(),
        };
        optionsByField.set(key, field);
      }
      field.options.push({
        chemicalId: entry.chemicalId,
        chemicalName: entry.chemicalName,
        display: displayStateFieldValue(stateField.value),
      });
      field.values.add(JSON.stringify(stateField.value ?? null));
    }
  }

  const stateFieldConflicts = Array.from(optionsByField.values())
    .filter((field) => field.values.size > 1)
    .map(({ jurisdiction, key, label, options }) => ({ jurisdiction, key, label, options }));

  return { storageLocations, stateFieldConflicts };
}

export type InventoryOverlapResolutionState = {
  strategy: InventoryOverlapResolutionStrategy;
  keepChemicalId?: string;
  fieldChoices: Record<string, string>;
};

/**
 * Initial per-overlap strategy: not-yet-filed years default to COMBINE (the
 * batch can be flipped in the UI). Identical-amount overlaps default to KEEP
 * first entry regardless — identical entries are almost always duplicate data
 * entry, and combining them would double-count. Filed years and
 * unit-mismatched overlaps also default to KEEP.
 */
export function initialInventoryOverlapResolutions(
  inventoryOverlaps: InventoryOverlap[]
): Record<string, InventoryOverlapResolutionState> {
  const resolutions: Record<string, InventoryOverlapResolutionState> = {};
  for (const inventoryOverlap of inventoryOverlaps) {
    const key = inventoryOverlapKey(inventoryOverlap);
    if (
      !inventoryOverlap.isFiled &&
      !inventoryOverlap.hasIdenticalAmounts &&
      inventoryOverlap.hasSharedUnit
    ) {
      resolutions[key] = {
        strategy: InventoryOverlapResolutionStrategy.Combine,
        fieldChoices: {},
      };
    } else {
      resolutions[key] = {
        strategy: InventoryOverlapResolutionStrategy.Keep,
        keepChemicalId: inventoryOverlap.hasIdenticalAmounts
          ? inventoryOverlap.entries[0]?.chemicalId
          : undefined,
        fieldChoices: {},
      };
    }
  }
  return resolutions;
}

export function isInventoryOverlapResolved(
  inventoryOverlap: InventoryOverlap,
  state: InventoryOverlapResolutionState | undefined
): boolean {
  if (!state) return false;
  if (state.strategy === InventoryOverlapResolutionStrategy.Keep) {
    return !!state.keepChemicalId;
  }
  if (!inventoryOverlap.hasSharedUnit) return false;

  const conflicts = findCombineConflicts(inventoryOverlap);
  return (
    conflicts.storageLocations.every((location) =>
      location.conflicts.every(
        (conflict) => !!state.fieldChoices[storageLocationConflictKey(location, conflict.attribute)]
      )
    ) &&
    conflicts.stateFieldConflicts.every(
      (conflict) => !!state.fieldChoices[stateFieldConflictKey(conflict.jurisdiction, conflict.key)]
    )
  );
}

export function combinedMaxAmountPreview(inventoryOverlap: InventoryOverlap): string {
  const unit = inventoryOverlap.entries.find((entry) => entry.unit)?.unit ?? null;
  const parts = inventoryOverlap.entries.map((entry) =>
    formatEntryAmount({ maxAmount: entry.maxAmount ?? 0, unit: entry.unit })
  );
  const total = inventoryOverlap.entries.reduce((sum, entry) => sum + (entry.maxAmount ?? 0), 0);
  return `${parts.join(" + ")} → ${formatEntryAmount({ maxAmount: total, unit })}`;
}

/**
 * Maps the UI resolution state to the mutation contract. Callers must gate on
 * isInventoryOverlapResolved for every overlap first; an unresolved overlap
 * here is a programming error.
 */
export function buildInventoryOverlapResolutionInputs(
  inventoryOverlaps: InventoryOverlap[],
  resolutions: Record<string, InventoryOverlapResolutionState>
): InventoryOverlapResolutionInput[] {
  return inventoryOverlaps.map((inventoryOverlap) => {
    const key = inventoryOverlapKey(inventoryOverlap);
    const state = resolutions[key];
    if (!state || !isInventoryOverlapResolved(inventoryOverlap, state)) {
      throw new Error(`Inventory overlap ${key} is not fully resolved.`);
    }

    if (state.strategy === InventoryOverlapResolutionStrategy.Combine) {
      const conflicts = findCombineConflicts(inventoryOverlap);
      const fieldResolutions: InventoryOverlapFieldResolutionInput[] = [
        ...conflicts.storageLocations.flatMap((location) =>
          location.conflicts.map((conflict) => {
            const chosenSerializedValue =
              state.fieldChoices[storageLocationConflictKey(location, conflict.attribute)];
            const chosenOption = conflict.options.find(
              (option) => option.serializedValue === chosenSerializedValue
            );
            if (!chosenOption) {
              throw new Error(`Inventory overlap ${key} is not fully resolved.`);
            }
            return {
              scope: InventoryOverlapFieldResolutionScope.StorageLocation,
              storageLocationId: location.storageLocationId,
              storageTypes: location.storageTypes,
              storageTypeDescription: location.storageTypeDescription,
              attribute: conflict.attribute,
              ...chosenOption.resolutionFields,
            };
          })
        ),
        ...conflicts.stateFieldConflicts.map((conflict) => ({
          scope: InventoryOverlapFieldResolutionScope.StateField,
          jurisdiction: conflict.jurisdiction,
          key: conflict.key,
          chosenFromChemicalId:
            state.fieldChoices[stateFieldConflictKey(conflict.jurisdiction, conflict.key)],
        })),
      ];
      return {
        facilityId: inventoryOverlap.facilityId,
        reportingYear: inventoryOverlap.reportingYear,
        strategy: InventoryOverlapResolutionStrategy.Combine,
        fieldResolutions,
      };
    }

    return {
      facilityId: inventoryOverlap.facilityId,
      reportingYear: inventoryOverlap.reportingYear,
      strategy: InventoryOverlapResolutionStrategy.Keep,
      keepChemicalId: state.keepChemicalId,
    };
  });
}
